import { Link } from 'react-router-dom';
import SEOPageLayout, { tokens } from '../../components/SEOPageLayout';
import { appLinks } from '../../lib/crossSiteLinks';

const sampleEvents = [
  {
    eventType: 'pull_request.opened',
    timestamp: '2026-01-02T09:15:22Z',
    repository: 'acme-corp/api-service',
    data: {
      number: 847,
      title: 'Add rate limiting to auth endpoints',
      author: 'jane.developer',
      targetBranch: 'main',
      description: 'Implements rate limiting to prevent brute force attacks on authentication endpoints.',
    },
  },
  {
    eventType: 'review.submitted',
    timestamp: '2026-01-02T13:45:22Z',
    repository: 'acme-corp/api-service',
    data: {
      pullRequest: 847,
      reviewer: 'senior.engineer',
      state: 'APPROVED',
      body: 'LGTM. Good implementation of the token bucket algorithm.',
    },
  },
  {
    eventType: 'check_suite.completed',
    timestamp: '2026-01-02T14:28:11Z',
    repository: 'acme-corp/api-service',
    data: {
      pullRequest: 847,
      conclusion: 'success',
      checks: [
        { name: 'build', conclusion: 'success', duration: '2m 34s' },
        { name: 'unit-tests', conclusion: 'success', duration: '4m 12s' },
        { name: 'integration-tests', conclusion: 'success', duration: '8m 45s' },
        { name: 'security-scan', conclusion: 'success', duration: '1m 22s' },
      ],
    },
  },
  {
    eventType: 'pull_request.merged',
    timestamp: '2026-01-02T14:32:18Z',
    repository: 'acme-corp/api-service',
    data: {
      number: 847,
      title: 'Add rate limiting to auth endpoints',
      author: 'jane.developer',
      mergedBy: 'jane.developer',
      mergeCommit: 'a1b2c3d4e5f6789...',
      approvals: 1,
      checksStatus: 'all_passed',
    },
  },
  {
    eventType: 'deployment.created',
    timestamp: '2026-01-02T14:45:33Z',
    repository: 'acme-corp/api-service',
    data: {
      environment: 'production',
      status: 'success',
      ref: 'main',
      sha: 'a1b2c3d4e5f6789...',
      deployedBy: 'github-actions[bot]',
    },
  },
];

export default function SampleTimelinePage() {
  return (
    <SEOPageLayout
      title="Sample Audit Timeline - Example Change Control Evidence"
      description="See an example of EvidentTrail's audit timeline showing software change events, approvals, CI checks, and deployments. Anonymized sample data for illustration."
    >
      {/* Hero */}
      <section className={tokens.section}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto">
            <h1 className={`${tokens.heading.h1} mb-6`}>
              Sample Audit Timeline
            </h1>
            <p className={tokens.text.large}>
              This is an example of what an EvidentTrail audit timeline looks like. 
              All data is anonymized and for illustration purposes only.
            </p>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className={tokens.sectionAlt}>
        <div className={tokens.container}>
          <div className="max-w-4xl mx-auto">
            <h2 className={`${tokens.heading.h2} mb-8`}>Timeline: January 2, 2026</h2>
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-slate-700" />
              
              {/* Events */}
              <div className="space-y-6">
                {sampleEvents.map((event, index) => (
                  <div key={index} className="relative pl-12">
                    {/* Timeline dot */}
                    <div className={`absolute left-2 w-4 h-4 rounded-full border-2 ${
                      event.eventType.includes('merged') || event.eventType.includes('deployment') 
                        ? 'bg-green-500 border-green-400' 
                        : event.eventType.includes('review')
                        ? 'bg-blue-500 border-blue-400'
                        : 'bg-slate-500 border-slate-400'
                    }`} />
                    
                    <div className={tokens.card}>
                      <div className="flex flex-wrap items-center gap-2 mb-3">
                        <span className="px-2 py-1 bg-slate-700 rounded text-xs font-mono text-slate-300">
                          {event.eventType}
                        </span>
                        <span className="text-xs text-slate-500">
                          {new Date(event.timestamp).toLocaleString()}
                        </span>
                        <span className="text-xs text-slate-600">
                          {event.repository}
                        </span>
                      </div>
                      <pre className="text-sm text-slate-300 overflow-x-auto bg-slate-900/50 p-4 rounded-lg">
                        {JSON.stringify(event.data, null, 2)}
                      </pre>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What This Shows */}
      <section className={tokens.section}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto">
            <h2 className={`${tokens.heading.h2} mb-6`}>What This Sample Demonstrates</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className={tokens.card}>
                <h3 className={`${tokens.heading.h3} mb-3`}>Complete Chain of Custody</h3>
                <p className={tokens.text.base}>
                  From PR opened → reviewed → checks passed → merged → deployed. 
                  Every step is recorded with timestamps.
                </p>
              </div>
              <div className={tokens.card}>
                <h3 className={`${tokens.heading.h3} mb-3`}>Identity Tracking</h3>
                <p className={tokens.text.base}>
                  Who wrote the code, who approved it, and who merged it — 
                  all captured automatically from GitHub.
                </p>
              </div>
              <div className={tokens.card}>
                <h3 className={`${tokens.heading.h3} mb-3`}>CI Evidence</h3>
                <p className={tokens.text.base}>
                  Check names, pass/fail status, and duration — proving that 
                  automated tests were run before deployment.
                </p>
              </div>
              <div className={tokens.card}>
                <h3 className={`${tokens.heading.h3} mb-3`}>Deployment Link</h3>
                <p className={tokens.text.base}>
                  The deployment event links the code merge to production, 
                  closing the audit loop.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Samples */}
      <section className={tokens.sectionAlt}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto">
            <h2 className={`${tokens.heading.h2} mb-6`}>Related Sample Outputs</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <Link to="/sample-drift-event" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
                <h3 className={`${tokens.heading.h3} mb-2`}>Sample Drift Event</h3>
                <p className={tokens.text.base}>See how branch protection drift changes are recorded.</p>
              </Link>
              <Link to="/sample-exception" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
                <h3 className={`${tokens.heading.h3} mb-2`}>Sample Exception Record</h3>
                <p className={tokens.text.base}>Review a documented exception with approvals and rationale.</p>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className={tokens.sectionAlt}>
        <div className={tokens.container}>
          <div className="max-w-2xl mx-auto text-center">
            <h2 className={`${tokens.heading.h2} mb-4`}>See Your Own Timeline</h2>
            <p className={`${tokens.text.base} mb-8`}>
              Connect your GitHub repositories and start generating real audit evidence.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href={appLinks.register} className="btn btn-primary text-lg px-8 py-3">
                Get Started
              </a>
              <Link to="/sample-drift-event" className="text-slate-300 hover:text-white transition-colors">
                See Sample Drift Event →
              </Link>
            </div>
          </div>
        </div>
      </section>
    </SEOPageLayout>
  );
}
